import { motion } from 'framer-motion';
import PageError from '../components/PageError';
import {
  MapPin,
  Shield,
  Users,
  Clock,
  Search,
  Filter,
  RefreshCw,
  Plus,
  Edit,
  Trash2,
  XCircle,
  Route,
  Home,
  Eye,
  Power,
  Copy,
  MoreHorizontal,
  Download,
  CheckCircle,
} from 'lucide-react';
import { exportToCsv } from '../utils/exportCsv';
import { createStaticMapUrl } from '../config/mapboxConfig';
import useStops, { formatStopCoordForDisplay } from '../hooks/trStops';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useSortPaginate } from '../hooks/useSortPaginate';
import { SortableHeader, TablePagination, ColumnToggle, useColumnVisibility } from '../components/TableControls';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdownMenu';
import DeleteImpactModal from '../components/DeleteImpactModal';
import { StatusBadge } from '../components/ui/StatusBadge';

const STOP_COLUMNS = [
  { key: 'adresse', label: 'Adresse' },
  { key: 'circuit', label: 'Circuit' },
  { key: 'eleves', label: 'Élèves' },
  { key: 'status', label: 'Statut' },
];

const StopManagementUnified = () => {
  const {
    filteredStops,
    pendingStops,
    loading,
    error,
    setError,
    stats,
    mapPreviewStop,
    setMapPreviewStop,
    deleteModalOpen,
    stopToDelete,
    filters,
    setFilters,
    loadStops,
    handleAdd,
    handleEdit,
    handleToggleStatus,
    handleApprove,
    handleReject,
    showPendingOnly,
    handleDelete,
    confirmDeleteStop,
    setDeleteModalOpen,
    handleCopyCoordinates,
    hasFeature,
  } = useStops();

  const { visibility, toggle, visible } = useColumnVisibility({
    adresse: true,
    circuit: true,
    eleves: true,
    status: true,
  });

  const { paginated, page, totalPages, totalItems, pageSize, sortCol, sortDir, setPage, toggleSort } = useSortPaginate(
    filteredStops,
    { col: 'stop_name', dir: 'asc' },
    15,
    [filters],
  );

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Arrêts</h1>
          <p className="text-gray-500">Gérez les points de ramassage et de dépose</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            type="button"
            className="hidden md:inline-flex"
            onClick={() =>
              exportToCsv(
                filteredStops,
                [
                  { label: 'Nom', value: (s) => s.stop_name || '' },
                  { label: 'Réf', value: (s) => s.ref || '' },
                  { label: 'Adresse', value: (s) => s.address || '' },
                  { label: 'Ville', value: (s) => s.city || '' },
                  { label: 'Type', value: (s) => s.type || '' },
                  { label: 'Capacité', value: (s) => s.capacity ?? '' },
                  { label: 'Statut', value: (s) => s.status || '' },
                  { label: 'Latitude', value: (s) => s.latitude ?? '' },
                  { label: 'Longitude', value: (s) => s.longitude ?? '' },
                ],
                'arrets.csv',
              )
            }
          >
            <Download size={14} />
            Exporter CSV
          </Button>
          <Button type="button" onClick={handleAdd}>
            <Plus size={14} />
            Ajouter un arrêt
          </Button>
        </div>
      </div>

      {/* Stats */}
      {hasFeature('metrics') && stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {[
            {
              icon: MapPin,
              bg: 'bg-green-100',
              text: 'text-green-600',
              label: 'Total Arrêts',
              value: stats.total_stops || 0,
            },
            {
              icon: Shield,
              bg: 'bg-blue-100',
              text: 'text-blue-600',
              label: 'Actifs',
              value: stats.active_stops || 0,
            },
            {
              icon: Users,
              bg: 'bg-purple-100',
              text: 'text-purple-600',
              label: 'Élèves Assignés',
              value: stats.total_students || 0,
            },
            {
              icon: Clock,
              bg: 'bg-orange-100',
              text: 'text-orange-600',
              label: 'Temps Moyen',
              value: `${stats.average_wait_time || 0} min`,
            },
          ].map(({ icon: Icon, bg, text, label, value }) => (
            <div
              key={label}
              className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex items-center space-x-4"
            >
              <div className={`p-3 ${bg} rounded-lg ${text}`}>
                <Icon size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">{label}</p>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bloc info — arrêts proposés par un chauffeur en attente de validation */}
      {pendingStops.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl"
        >
          <Clock className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-blue-800">Vous avez des arrêts en attente</p>
            <p className="text-xs text-blue-700">
              Un ou plusieurs chauffeurs ont proposé un nouvel arrêt depuis leur application. Validez-les pour les
              rendre disponibles.
            </p>
          </div>
          <button
            type="button"
            onClick={showPendingOnly}
            className="shrink-0 px-3 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
          >
            Voir les arrêts en attente
          </button>
        </motion.div>
      )}

      {/* Filtres */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher par nom, adresse, description..."
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
            {filters.search && (
              <button
                onClick={() => setFilters((prev) => ({ ...prev, search: '' }))}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtres:</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                {
                  value: '',
                  label: 'Tous',
                  active: 'bg-blue-100 text-blue-800',
                },
                {
                  value: 'active',
                  label: 'Actifs',
                  active: 'bg-green-100 text-green-800',
                },
                {
                  value: 'inactive',
                  label: 'Inactifs',
                  active: 'bg-gray-100 text-gray-800',
                },
                {
                  value: 'pending',
                  label: 'En attente',
                  active: 'bg-yellow-100 text-yellow-800',
                },
              ].map(({ value, label, active }) => (
                <button
                  key={label}
                  onClick={() => setFilters((prev) => ({ ...prev, status: value }))}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    filters.status === value ? active : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={loadStops}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw size={14} />
            </button>
            <ColumnToggle columns={STOP_COLUMNS} visibility={visibility} onToggle={toggle} />
          </div>
        </div>
      </div>

      {/* Tableau / Cards */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {error && (
          <PageError
            error={error}
            onRetry={() => {
              setError(null);
              loadStops();
            }}
            variant="inline"
          />
        )}
        {!error && (
          <>
            {/* ── Vue cartes (mobile < md) ── */}
            <div className="md:hidden divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={`skel-m-${i}`} className="p-4 flex items-start gap-3">
                    <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-3 w-28" />
                      <Skeleton className="h-3 w-36" />
                    </div>
                  </div>
                ))
              ) : filteredStops.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun arrêt trouvé</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    {filters.search || filters.status
                      ? 'Aucun arrêt ne correspond aux critères de recherche.'
                      : 'Commencez par ajouter votre premier arrêt.'}
                  </p>
                  {!filters.search && !filters.status && (
                    <Button type="button" onClick={handleAdd}>
                      <Plus size={14} />
                      Ajouter un arrêt
                    </Button>
                  )}
                </div>
              ) : (
                paginated.map((stop) => (
                  <motion.div
                    key={stop.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-4 hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                          <MapPin className="text-blue-600" size={20} />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 truncate" title={stop.stop_name}>
                            {stop.stop_name}
                          </div>
                          {stop.ref && <div className="text-xs text-gray-500 font-mono">{stop.ref}</div>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <StatusBadge status={stop.status} />
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              aria-label="Actions"
                              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                            >
                              <MoreHorizontal size={16} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            {stop.latitude != null && stop.longitude != null && (
                              <DropdownMenuItem onClick={() => setMapPreviewStop(stop)}>
                                <Eye size={14} className="mr-2" />
                                Voir sur la carte
                              </DropdownMenuItem>
                            )}
                            {(stop.status || '').toLowerCase() === 'pending' ? (
                              <>
                                <DropdownMenuItem onClick={() => handleApprove(stop)}>
                                  <CheckCircle size={14} className="mr-2 text-green-600" />
                                  Valider
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleReject(stop)}>
                                  <XCircle size={14} className="mr-2 text-red-600" />
                                  Rejeter
                                </DropdownMenuItem>
                              </>
                            ) : (
                              <DropdownMenuItem onClick={() => handleToggleStatus(stop)}>
                                <Power size={14} className="mr-2" />
                                {(stop.status || '').toLowerCase() === 'active' ? 'Désactiver' : 'Activer'}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleEdit(stop)}>
                              <Edit size={14} className="mr-2" />
                              Modifier
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDelete(stop)}
                              className="text-red-600 focus:text-red-600 focus:bg-red-50"
                            >
                              <Trash2 size={14} className="mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    <div className="mt-2.5 ml-[52px] space-y-1.5">
                      {stop.address && (
                        <div className="flex items-start gap-1.5">
                          <Home size={13} className="text-gray-400 shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-600 line-clamp-1">{stop.address}</span>
                        </div>
                      )}
                      {stop.active_routes?.length > 0 && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Route size={13} className="text-gray-400 shrink-0" />
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {stop.active_routes.length} circuit
                            {stop.active_routes.length > 1 ? 's' : ''}
                          </span>
                          <span className="text-xs text-gray-500 truncate max-w-[160px]">
                            {stop.active_routes[0].name}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Users size={13} className="text-gray-400 shrink-0" />
                        {stop.students?.length > 0 ? (
                          <>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {stop.students.length} élève
                              {stop.students.length > 1 ? 's' : ''}
                            </span>
                            <span className="text-xs text-gray-500 max-w-[180px] flex flex-wrap gap-x-1">
                              {stop.students.slice(0, 2).map((s, i) => (
                                <span key={s.id || i} className="inline-flex items-center gap-1">
                                  {i > 0 && <span>,</span>}
                                  {s.first_name} {s.last_name}
                                  {s.status === 'inactive' && (
                                    <span className="inline-flex items-center px-1.5 py-0 rounded text-[10px] font-medium bg-gray-100 text-gray-500">
                                      Inactif
                                    </span>
                                  )}
                                </span>
                              ))}
                              {stop.students.length > 2 ? ` +${stop.students.length - 2}` : ''}
                            </span>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Aucun élève</span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* ── Tableau (desktop ≥ md) ── */}
            <div className="overflow-x-auto hidden md:block">
              <table className="min-w-[580px] w-full table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader col="stop_name" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>
                      Arrêt
                    </SortableHeader>
                    {visible('adresse') && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Adresse
                      </th>
                    )}
                    {visible('circuit') && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Circuit
                      </th>
                    )}
                    {visible('eleves') && (
                      <SortableHeader col="student_count" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>
                        Élèves
                      </SortableHeader>
                    )}
                    {visible('status') && (
                      <SortableHeader
                        col="is_active"
                        sortCol={sortCol}
                        sortDir={sortDir}
                        onSort={toggleSort}
                        className="w-24 whitespace-nowrap"
                      >
                        Statut
                      </SortableHeader>
                    )}
                    <th className="px-4 py-3 w-28 whitespace-nowrap text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <tr key={`skel-${i}`}>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <Skeleton className="h-10 w-10 rounded-full" />
                            <div className="space-y-1.5">
                              <Skeleton className="h-3.5 w-24" />
                              <Skeleton className="h-3 w-16" />
                            </div>
                          </div>
                        </td>
                        {visible('adresse') && (
                          <td className="px-4 py-4">
                            <Skeleton className="h-3.5 w-36" />
                          </td>
                        )}
                        {visible('circuit') && (
                          <td className="px-4 py-4">
                            <Skeleton className="h-3.5 w-24" />
                          </td>
                        )}
                        {visible('eleves') && (
                          <td className="px-4 py-4">
                            <Skeleton className="h-3.5 w-8" />
                          </td>
                        )}
                        {visible('status') && (
                          <td className="px-4 py-4">
                            <Skeleton className="h-5 w-14 rounded-full" />
                          </td>
                        )}
                        <td className="px-4 py-4">
                          <div className="flex justify-end">
                            <Skeleton className="h-7 w-7 rounded" />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : filteredStops.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun arrêt trouvé</h3>
                        <p className="text-sm text-gray-500 mb-4">
                          {filters.search || filters.status
                            ? 'Aucun arrêt ne correspond aux critères de recherche.'
                            : 'Commencez par ajouter votre premier arrêt.'}
                        </p>
                        {!filters.search && !filters.status && (
                          <Button type="button" onClick={handleAdd}>
                            <Plus size={14} />
                            Ajouter un arrêt
                          </Button>
                        )}
                      </td>
                    </tr>
                  ) : (
                    paginated.map((stop) => (
                      <motion.tr
                        key={stop.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                              <MapPin className="text-blue-600" size={20} />
                            </div>
                            <div className="ml-4 min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 line-clamp-2" title={stop.stop_name}>
                                {stop.stop_name}
                              </div>
                              {stop.ref && <div className="text-xs text-gray-500">{stop.ref}</div>}
                            </div>
                          </div>
                        </td>
                        {visible('adresse') && (
                          <td className="px-4 py-4">
                            {stop.address ? (
                              <div className="flex items-start gap-1">
                                <Home size={14} className="text-gray-400 shrink-0 mt-1" />
                                <div className="text-sm text-gray-900 line-clamp-2">{stop.address}</div>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                        )}
                        {visible('circuit') && (
                          <td className="px-4 py-4">
                            {stop.active_routes && stop.active_routes.length > 0 ? (
                              <div className="space-y-1">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {stop.active_routes.length} circuit
                                  {stop.active_routes.length > 1 ? 's' : ''}
                                </span>
                                <div className="text-xs text-gray-600">
                                  {stop.active_routes.slice(0, 2).map((route, i) => (
                                    <div key={route.id || i} className="flex items-center gap-1">
                                      <Route size={14} className="text-gray-400" />
                                      {route.name}
                                    </div>
                                  ))}
                                  {stop.active_routes.length > 2 && (
                                    <div className="text-[11px] text-gray-400">
                                      +{stop.active_routes.length - 2} autre(s)
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        )}
                        {visible('eleves') && (
                          <td className="px-4 py-4 whitespace-nowrap">
                            {stop.students && stop.students.length > 0 ? (
                              <div className="space-y-1">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {stop.students.length} élève
                                  {stop.students.length > 1 ? 's' : ''}
                                </span>
                                <div className="text-xs text-gray-600">
                                  {stop.students.slice(0, 2).map((s, i) => (
                                    <div key={s.id || i} className="flex items-center gap-1 flex-wrap">
                                      <span>
                                        {s.first_name} {s.last_name}
                                      </span>
                                      {s.class_name && (
                                        <span>
                                          {' '}
                                          — {s.class_name}
                                          {s.grade_level ? ` (${s.grade_level})` : ''}
                                        </span>
                                      )}
                                      {s.status === 'inactive' && (
                                        <span className="inline-flex items-center px-1.5 py-0 rounded text-[10px] font-medium bg-gray-100 text-gray-500">
                                          Inactif
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                  {stop.students.length > 2 && (
                                    <div className="text-[11px] text-gray-400">
                                      +{stop.students.length - 2} autre(s)
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">Aucun élève</span>
                            )}
                          </td>
                        )}
                        {visible('status') && (
                          <td className="px-4 py-4 whitespace-nowrap">
                            <StatusBadge status={stop.status} />
                          </td>
                        )}
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                aria-label="Actions"
                                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                              >
                                <MoreHorizontal size={16} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              {stop.latitude != null && stop.longitude != null && (
                                <DropdownMenuItem onClick={() => setMapPreviewStop(stop)}>
                                  <Eye size={14} className="mr-2" />
                                  Voir sur la carte
                                </DropdownMenuItem>
                              )}
                              {(stop.status || '').toLowerCase() === 'pending' ? (
                                <>
                                  <DropdownMenuItem onClick={() => handleApprove(stop)}>
                                    <CheckCircle size={14} className="mr-2 text-green-600" />
                                    Valider
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleReject(stop)}>
                                    <XCircle size={14} className="mr-2 text-red-600" />
                                    Rejeter
                                  </DropdownMenuItem>
                                </>
                              ) : (
                                <DropdownMenuItem onClick={() => handleToggleStatus(stop)}>
                                  <Power size={14} className="mr-2" />
                                  {(stop.status || '').toLowerCase() === 'active' ? 'Désactiver' : 'Activer'}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleEdit(stop)}>
                                <Edit size={14} className="mr-2" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDelete(stop)}
                                className="text-red-600 focus:text-red-600 focus:bg-red-50"
                              >
                                <Trash2 size={14} className="mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {!loading && filteredStops.length > 0 && (
              <TablePagination
                page={page}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>

      {/* Map preview overlay */}
      {mapPreviewStop && mapPreviewStop.latitude != null && mapPreviewStop.longitude != null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-label="Aperçu carte"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-4 space-y-3">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-gray-900">{mapPreviewStop.stop_name}</h3>
              <button
                type="button"
                onClick={() => setMapPreviewStop(null)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                aria-label="Fermer"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <img
              src={createStaticMapUrl(
                {
                  lat: parseFloat(mapPreviewStop.latitude),
                  lng: parseFloat(mapPreviewStop.longitude),
                },
                15,
                '800x480',
                'streets-v12',
                {
                  marker: true,
                  markerColor: '2563eb',
                  hideAttribution: true,
                  hideLogo: true,
                },
              )}
              alt={`Carte ${mapPreviewStop.stop_name}`}
              className="w-full rounded-lg border border-gray-200"
            />
            <div
              className="flex w-full items-center gap-3 rounded-xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50/80 px-4 py-3 shadow-sm"
              role="status"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white shadow-md">
                <MapPin className="h-5 w-5" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-bold uppercase tracking-wider text-blue-800">Aperçu Statique</p>
                <p className="mt-0.5 font-mono text-base font-semibold leading-snug tracking-tight text-gray-900 tabular-nums">
                  {formatStopCoordForDisplay(mapPreviewStop.latitude)}
                  <span className="mx-1.5 font-sans font-normal text-gray-400">·</span>
                  {formatStopCoordForDisplay(mapPreviewStop.longitude)}
                </p>
                <p className="mt-0.5 text-xs text-gray-600">Latitude · longitude (WGS 84)</p>
              </div>
              <button
                type="button"
                onClick={() => handleCopyCoordinates(mapPreviewStop)}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-700 bg-white/70 border border-blue-200 rounded-md hover:bg-white sm:shrink-0"
              >
                <Copy size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteModalOpen && stopToDelete && (
        <DeleteImpactModal
          entity={stopToDelete}
          entityType="stop"
          entityName={stopToDelete.stop_name}
          onConfirm={confirmDeleteStop}
          onCancel={() => setDeleteModalOpen(false)}
        />
      )}
    </div>
  );
};

export default StopManagementUnified;
